import { useEffect, useRef } from 'preact/hooks';
import { useStore } from '../store';

/**
 * Manual expedition stages: when the sim parks a track/capture stage (awaitingAdvance) and the
 * player then finishes the matching field minigame, report its real outcome with
 * expedition.advance. The outcome must be newer than the moment the stage began waiting, so a
 * sticky result from an earlier session is never replayed. Never sends a result the sim did not produce.
 */
const STAGE_KIND = { track: 'tracking', capture: 'capture' } as const;
export function ExpeditionAdvance() {
  const { sim, bridge } = useStore();
  const ex = sim.expedition;
  const waitingSince = useRef<{ stage: string; tick: number } | null>(null);
  const stageStart = ex?.phaseStartTick;
  const sentTick = useRef(-1);
  const stage = ex !== null && ex.awaitingAdvance && ex.mode === 'manual' ? ex.status : null;
  const watched = stage === 'track' || stage === 'capture' ? stage : null;
  useEffect(() => {
    if (watched === null) { waitingSince.current = null; return; }
    // Prefer the sim's stage start tick; fall back to when the UI first saw the stage waiting (older sims).
    if (stageStart !== undefined) { waitingSince.current = { stage: watched, tick: stageStart }; }
    else if (waitingSince.current?.stage !== watched) { waitingSince.current = { stage: watched, tick: sim.tick }; }
    const o = sim.minigameOutcome;
    if (o === null || o.kind !== STAGE_KIND[watched]) return;
    if (o.tick <= waitingSince.current.tick || o.tick <= sentTick.current) return;
    sentTick.current = o.tick;
    bridge.send('expedition.advance', { result: o.result });
  }, [watched, stageStart, sim.tick, sim.minigameOutcome, bridge]);
  return null;
}
